import { useField } from "formik";
import { FC, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useNotifications } from "@hooks";
import { fileTypeErrorNotification } from "@dirootie-web/components-types";
import { IconTalTech } from "@components-common/icons";
import { configSettings } from "../../../configuration/ConfigurationSettings";

function bytesToMB(bytes: number) {
  return bytes / (1024 * 1024);
}

type IInputFileUploadProps = {
  id?: string;
  text?: string;
  accept?: any;
  type?: string;
  handleFileFromOutside?: boolean;
  setFile?: any;
};

export const InputFileUpload: FC<IInputFileUploadProps> = ({
  handleFileFromOutside = false,
  ...props
}) => {
  const { type = "IMG" } = props;
  const userNotificationService = useNotifications();
  const [fieldFileName, helpersFileName, metaFileName] = useField("fileName");
  const [fieldContents, helpersContents, metaContents] = useField("contents");

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0] || null;
    if (file === null) {
      return;
    }
    if (handleFileFromOutside && props.setFile) {
      props.setFile(file);
    } else convertToBase64String(file);
  }, []);

  const { acceptedFiles, getRootProps, getInputProps, isDragActive } =
    useDropzone({
      accept: props.accept ?? {},
      onDrop: onDrop,
      onDropRejected(fileRejections) {
        userNotificationService.error({
          title: fileRejections[0].errors[0].message,
        });
      },
      multiple: false,
      onDragOver: () => console.log("A"),
      minSize: configSettings.minFileSize,
      maxSize: configSettings.maxFileSize,
    });

  if (!props.text) {
    props.text = "Drag and drop a file here or click to upload";
  }

  const convertToBase64String = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (reader.result instanceof ArrayBuffer) {
          const base64String = arrayBufferToBase64(reader.result);
          metaFileName.setValue(file.name);
          const base64 =
            type === "IMG"
              ? `data:image/png;base64,${base64String}`
              : base64String;
          metaContents.setValue(base64);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  return (
    <>
      <div
        className={`
        dropzone-container p-0
        ${isDragActive ? "ebd-active-drag" : "container ebd-file-container"}
        `}
      >
        <div
          {...getRootProps({
            className:
              "disabled d-flex flex-column justify-content-center align-items-center w-100 h-100",
          })}
        >
          <input id={props.id} {...getInputProps()} />
          <div style={{ width: "50px", height: "50px" }}>
            <IconTalTech kind="upload" />
          </div>
          <p className="mb-0 mt-1">
            {acceptedFiles.length > 0
              ? "Click again to browse files or drop a file to modify the current one"
              : props.text}
          </p>
          {acceptedFiles.length > 0 && (
            <div className="w-100">
              {acceptedFiles.map((file) => {
                return (
                  <div className={"ebd-file-element"} key={file.name}>
                    {file.name}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <span style={{ fontSize: ".85rem" }}>
        Maximum file size: {bytesToMB(configSettings.maxFileSize)}MB.
      </span>
    </>
  );
};
